import { IAuthMeV2OutputDto, Permission, TypologiesEnum } from '@quadminds/shared/auth-system';
import { clearTokensFromStorage, validateTokenExp, jwtDecode } from '@quadminds/shared/utils';
import axios, { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useCheckSession from './useCheckSession';
import { useGetUserInfo } from './useGetUserInfo';
import { useRefreshToken } from './useRefreshToken';
import { MINDY_APP_URL } from '../config';
import { rememberAccessToken } from '../utils/agentsRequest';

interface AuthSystemOptions {
    validatePermissions?: Permission[];
    version?: 1 | 2;
    redirectUrl?: string;
    debug?: boolean;
    isEmbedded?: boolean;
}

const DEFAULT_OPTIONS: AuthSystemOptions = {
    version: 2,
    validatePermissions: [],
    debug: process.env['NODE_ENV'] !== 'production', // Default to false in production
    isEmbedded: false,
};

const validateUserPermissions = (permissions: Permission[] = [], user?: IAuthMeV2OutputDto): boolean => {
    if (!permissions.length) return true;
    return permissions.every((permission) => user?.permissions.includes(permission));
};

export const useAuthSystem = (options: AuthSystemOptions = DEFAULT_OPTIONS) => {
    const mergedOptions = useMemo(() => ({ ...DEFAULT_OPTIONS, ...options }), [options]);
    const debug = mergedOptions.debug;
    const didMount = useRef(false);
    const [version, setVersion] = useState<number>(mergedOptions.version || 2);
    const retryCount = useRef(0);
    const maxRetries = 5; // Número de reintentos antes de mostrar error

    const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);

    /****************************** */
    /* Define hooks */
    /****************************** */
    // Using custom hooks for session check and token refresh
    const { mutateAsync: checkSession, ...checkSessionQuery } = useCheckSession();
    const { mutateAsync: refreshToken, ...refreshTokenQuery } = useRefreshToken();

    // Using custom hook to get user info
    const {
        data: user,
        isLoading: isPending,
        error: userError,
        status,
        refetch: refetchUserInfo,
    } = useGetUserInfo(version, { refetchOnWindowFocus: false });

    const clearAuthState = useCallback((): void => {
        clearTokensFromStorage();
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('isStorkUser');
    }, []);

    const handleAuthRedirect = useCallback((): void => {
        clearAuthState();
        const redirectPath = mergedOptions.redirectUrl || '/';
        const redirectBack = globalThis.location.href || MINDY_APP_URL;
        const fullRedirectUrl = `${redirectPath}?logout=true&redirect=${redirectBack}`;

        if (debug) console.log('[Auth] Redirecting to login:', fullRedirectUrl);
        setTimeout(() => {
            globalThis.location.href = fullRedirectUrl;
        }, 100);
    }, [mergedOptions.redirectUrl, clearAuthState, debug]);

    const handleAuthError = useCallback(
        (error: unknown) => {
            if (debug) console.error('[Auth] Auth error encountered.');
            if (axios.isAxiosError(error)) {
                if (debug) {
                    console.error('[Auth] Axios error details:');
                    console.error(`[Auth] Message: ${error.message}`);
                    if (error.response) {
                        console.error(`[Auth] Status: ${error.response.status}`);
                        console.error(`[Auth] Data: ${JSON.stringify(error.response.data)}`);
                    }
                    if (error.config) {
                        console.error(`[Auth] Request URL: ${error.config.url}`);
                        console.error(`[Auth] Request Method: ${error.config.method}`);
                    }
                }

                if (error.message === 'Network Error' || !error.response) {
                    if (debug) console.warn('[Auth] Network error or no response received.');
                    // Potentially handle offline scenario or server down
                }
                if (error.response?.status === 401 || error.response?.status === 403) {
                    if (debug)
                        console.warn(
                            `[Auth] Unauthorized (401) or Forbidden (403) error. Status: ${error.response?.status}. Redirecting.`,
                        );
                }
            } else {
                if (debug) console.error('[Auth] Non-Axios error:', error);
            }
        },
        [debug],
    );

    const removeQueryParam = (param: string): void => {
        const url = new URL(globalThis.location.href);
        url.searchParams.delete(param);
        globalThis.history.pushState({}, '', url);
    };

    const storeTokens = (result: { accessToken?: string; refreshToken?: string } | undefined): void => {
        if (result?.accessToken) {
            rememberAccessToken(result.accessToken);
        }
        if (result?.refreshToken) {
            localStorage.setItem('refreshToken', result.refreshToken);
        }
    };

    const initializeAuth = useCallback(async (): Promise<boolean> => {
        try {
            setIsAuthenticating(true);
            let authTokenSuccessfullyResolved = false;
            let auxVersion = version;

            const urlParams = new URLSearchParams(globalThis.location.search);
            const urlToken = urlParams.get('token');
            const localToken = localStorage.getItem('token');
            const localRefreshToken = localStorage.getItem('refreshToken');
            const isE2e = process.env['NX_IS_E2E'] === 'true';
            // Check if user is already authenticated
            if (user) {
                if (debug) console.warn('[Auth] User already exists, skipping authentication.');
                setIsAuthenticating(false);
                return true;
            }

            // Check if localStorage token exists and is valid
            if (localToken) {
                const decoded = jwtDecode(localToken);
                if (validateTokenExp(decoded)) {
                    authTokenSuccessfullyResolved = true;
                    if (debug) console.log('[Auth] Valid token found in localStorage.');
                } else {
                    if (debug) console.warn('[Auth] Token in localStorage is expired.');
                }
            }

            // If token is missing/expired, try refresh flow with localStorage refresh token first
            if (!authTokenSuccessfullyResolved && localRefreshToken) {
                try {
                    const refreshedToken = (await refreshToken()) as {
                        accessToken?: string;
                        refreshToken?: string;
                    };

                    if (refreshedToken?.accessToken) {
                        const decodedRefreshedToken = jwtDecode(refreshedToken.accessToken);
                        if (validateTokenExp(decodedRefreshedToken) || isE2e) {
                            if (debug) console.log('[Auth] Token refreshed from localStorage refreshToken.');
                            storeTokens(refreshedToken);
                            if (version !== 2) {
                                auxVersion = 2;
                            }
                            authTokenSuccessfullyResolved = true;
                        } else {
                            if (debug) console.warn('[Auth] Refreshed token is expired. Clearing tokens.');
                            clearAuthState();
                        }
                    }
                } catch (error) {
                    if (debug) console.warn('[Auth] Local refresh token flow failed, falling back to session.', error);
                }
            }

            // If no valid token, check for token in URL
            if (!authTokenSuccessfullyResolved) {
                if (urlToken) {
                    if (debug) console.log('[Auth] Found token in URL, using it for authentication.', urlToken);
                    rememberAccessToken(urlToken);
                    removeQueryParam('token');
                    if (version !== 2) {
                        auxVersion = 2;
                    }
                    const decodedUrlToken = jwtDecode(urlToken);
                    if (validateTokenExp(decodedUrlToken) || isE2e) {
                        authTokenSuccessfullyResolved = true;
                    } else {
                        if (debug) console.warn('[Auth] Token in URL is expired.');
                        clearAuthState();
                        // Si es embedded, no redirigir, solo retornar false
                        if (mergedOptions.isEmbedded) {
                            setIsAuthenticating(false);
                            return false;
                        }
                        setIsAuthenticating(false);
                        handleAuthRedirect();
                        return false;
                    }
                } else {
                    try {
                        const tokenFromSession = (await checkSession()) as {
                            accessToken?: string;
                            refreshToken?: string;
                        };
                        if (tokenFromSession?.accessToken) {
                            const decodedSessionToken = jwtDecode(tokenFromSession.accessToken);
                            if (validateTokenExp(decodedSessionToken)) {
                                if (debug) console.log('[Auth] Session check successful, token retrieved.');
                                storeTokens(tokenFromSession);
                                if (version !== 1) {
                                    auxVersion = 1;
                                }
                                authTokenSuccessfullyResolved = true;
                            } else {
                                if (debug) console.warn('[Auth] Session token is expired.');
                            }
                        } else {
                            if (debug)
                                console.warn('[Auth] Session check did not return a valid token.', tokenFromSession);
                        }
                    } catch (error) {
                        if (debug) console.warn('[Auth] Session check failed, attempting to get token.', error);
                        // Si es embedded, no redirigir, solo retornar false
                        if (mergedOptions.isEmbedded) {
                            setIsAuthenticating(false);
                            return false;
                        }
                        setIsAuthenticating(false);
                        handleAuthRedirect();
                        return false;
                    }
                }
            }

            if (authTokenSuccessfullyResolved) {
                if (debug) {
                    console.log('[Auth] Auth success getting user token ');
                    console.log('[Auth] Fetching user info from version ', auxVersion);
                }
                setVersion(auxVersion);

                try {
                    await refetchUserInfo();
                    retryCount.current = 0; // Reset retry count on success
                    setIsAuthenticating(false);
                    return true;
                } catch (refetchError) {
                    // Retry logic ANTES de fallar
                    if (retryCount.current < maxRetries) {
                        retryCount.current++;
                        if (debug) console.warn(`[Auth] Retry ${retryCount.current}/${maxRetries} fetching user info`);
                        // Esperar antes de reintentar (backoff exponencial)
                        await new Promise((resolve) => setTimeout(resolve, 500 * retryCount.current));
                        // Reintentar recursivamente - NO cambiar isAuthenticating aquí
                        return initializeAuth();
                    } else {
                        // Solo después de agotar reintentos
                        if (debug) console.error('[Auth] Max retries reached, handling auth error');
                        setIsAuthenticating(false);
                        handleAuthError(refetchError);
                        // Si es embedded, no redirigir
                        if (!mergedOptions.isEmbedded) {
                            handleAuthRedirect();
                        }
                        return false;
                    }
                }
            } else {
                setIsAuthenticating(false);
                // Si es embedded, no redirigir
                if (!mergedOptions.isEmbedded) {
                    handleAuthRedirect();
                }
                return false;
            }
        } catch (error) {
            setIsAuthenticating(false);
            handleAuthError(error);
            // Si es embedded, no redirigir
            if (!mergedOptions.isEmbedded) {
                handleAuthRedirect();
            }
            return false;
        }
    }, [
        checkSession,
        clearAuthState,
        handleAuthError,
        handleAuthRedirect,
        refetchUserInfo,
        user,
        version,
        debug,
        mergedOptions.isEmbedded,
    ]);

    useEffect(() => {
        if (didMount.current || user || mergedOptions.isEmbedded) {
            return;
        }

        didMount.current = true;
        initializeAuth();
    }, [initializeAuth, user, mergedOptions.isEmbedded]);

    useEffect(() => {
        if (user?.typology === TypologiesEnum.MINDY_CLARO_KNOWLEDGE_USER && version !== 2) {
            setVersion(2);
        }
    }, [user, version]);

    const hasPermissions = validateUserPermissions(mergedOptions.validatePermissions, user);

    const isLoading = useMemo(() => {
        return isAuthenticating || isPending || status === 'pending';
    }, [isAuthenticating, isPending, status]);

    const error: Error | AxiosError | null = useMemo(
        () => userError || checkSessionQuery.error || refreshTokenQuery.error,
        [userError, checkSessionQuery.error, refreshTokenQuery.error],
    );

    const logout = useCallback((): void => {
        clearAuthState();
        handleAuthRedirect();
    }, [clearAuthState, handleAuthRedirect]);

    useEffect(() => {
        if (error && debug && !mergedOptions.isEmbedded) console.error('[Auth] Error state:', error);
    }, [error, debug, mergedOptions.isEmbedded]);

    const hasAccessToMindy = user?.applications.includes('mindy') ?? true;

    return {
        user,
        hasPermissions,
        isLoading,
        error,
        refreshAuth: refreshToken,
        didMount,
        logout,
        checkSessionQuery,
        refreshTokenQuery,
        clearAuthState,
        initializeAuth,
        refetchUserInfo,
        checkSession,
        hasAccessToMindy,
    };
};
